import { useEffect, useMemo } from "react";
import { Flex } from "@similarweb/ui-components/foundations";
import { useLoading } from "app/custom-hooks/loadingHook";
import { TrafficAndEngagementHighLevelDataInner } from "../../TrafficAndEngagementHighLevelData";
import { WebsiteRankTable } from "app/pages/website-analysis/TrafficAndEngagement/Components/single/WebsiteRankTable";
import type { ITrafficSingleBetaModeContainer } from "app/pages/website-analysis/TrafficAndEngagement/types";
import { AudienceWidgetSingleWithBeta } from "app/pages/website-analysis/TrafficAndEngagement/Components/AudienceWidgetSingleWithBeta";
import { column } from "app/components/React/Table/SWReactTableDefaults";

export const TrafficSingleBetaModeContainer = (props: ITrafficSingleBetaModeContainer) => {
    const { showGAApprovedData, subtitleFilters, apiParams, isGAPrivate, websiteAnalysisApiService } = props;
    const { webSource, country, key, isWindow, isWWW, to, from } = apiParams;

    const apiParamsObj = useMemo(() => {
        return {
            country,
            from,
            to,
            includeSubDomains: !isWWW,
            isWindow,
            keys: key,
            timeGranularity: "Monthly",
            webSource,
        };
    }, [country, from, to, isWWW, isWindow, key, webSource]);
    const [webRanksData, webRanksDataOps] = useLoading();
    useEffect(() => {
        webRanksDataOps.load(() =>
            websiteAnalysisApiService.getWebRanksData({
                ...apiParamsObj,
            }),
        );
    }, [webSource, country, key, isWindow, isWWW, to, from]);

    const isWebRanksLoading = [useLoading.STATES.INIT, useLoading.STATES.LOADING].includes(webRanksData.state);
    const isWebRanksFetchError = webRanksData.state === useLoading.STATES.ERROR;
    const totalVisitsTableDataColumn = useMemo(() => {
        return column({
            field: "TotalVisits",
            displayName: "Visits",
            cellTemplate: "default-cell-right-align",
            headerCellTemplate: "default-cell-header-right-align",
            format: "minVisitsAbbr",
        });
    }, []);
    const deviceDistributionTableDataColumn = useMemo(() => {
        return column({
            field: "DesktopMobileShareVisits",
            displayName: "Desktop                    Mobile",
            cellTemplate: "percentage-bar-cell-rounded",
            headerCellTemplate: "desktop-mobile-header-cell",
            headerCellClass: "u-no-pointer-events",
            showTotalCount: false,
            cellClass: "no-ga-verified-icon",
            normalizeValueForComparison: (value) => value[0],
        });
    }, []);

    return (
        <>
            <Flex
                justifyContent="space-between"
                flexWrap="wrap"
                columnGap="24px"
            >
                <AudienceWidgetSingleWithBeta
                    subtitleFilters={subtitleFilters}
                    params={apiParamsObj}
                    hasGA={showGAApprovedData}
                    isGAPrivate={isGAPrivate}
                    tableDataColumn={totalVisitsTableDataColumn}
                    metric={"EngagementVisits"}
                    titleTooltip={"wa.ao.totalvisits.tooltip"}
                    title={"wa.ao.totalvisits"}
                />
                <AudienceWidgetSingleWithBeta
                    subtitleFilters={subtitleFilters}
                    params={apiParamsObj}
                    hasGA={showGAApprovedData}
                    isGAPrivate={isGAPrivate}
                    tableDataColumn={deviceDistributionTableDataColumn}
                    metric={"EngagementDesktopVsMobileVisits"}
                    titleTooltip={"wa.ao.trafficshare.tooltip"}
                    title={"wa.ao.trafficshare"}
                />
                <WebsiteRankTable
                    isError={isWebRanksFetchError}
                    data={webRanksData.data}
                    params={apiParams}
                    isLoading={isWebRanksLoading}
                />
            </Flex>
            <TrafficAndEngagementHighLevelDataInner {...props} />
        </>
    );
};
